import { computed, reactive, watch } from 'vue';
import { useRoute } from 'vue-router';

// Chiave localStorage per le impostazioni layout (come per il dark theme).
// darkTheme NON è incluso: resta gestito/persistito da dashStore (vedi AppLayout).
const LAYOUT_STORAGE_KEY = 'immigreer.layoutConfig';

// Impostazioni di DEFAULT del layout (usate anche dal pulsante "Ripristina predefinite").
const LAYOUT_DEFAULTS = {
    primary: 'indigo',
    surface: null,
    darkTheme: false,
    menuMode: 'static',
    menuTheme: 'light',
    topbarTheme: 'indigo',
    menuProfilePosition: 'end'
};

const layoutConfig = reactive({ ...LAYOUT_DEFAULTS });

// Mentre si applica un aspetto NON scelto dall'utente (i default d'azienda, o il ripristino) la
// persistenza va sospesa: altrimenti il watch riscrive la chiave e l'utente risulterebbe
// «personalizzato» pur non avendo toccato nulla. Dichiarata qui perché `let` non è utilizzabile
// prima della sua riga.
let sospendiPersistenza = false;

/**
 * Vero se su QUESTO dispositivo l'utente non ha mai toccato le impostazioni. È la condizione per
 * applicargli l'aspetto scelto dall'azienda: chi si è fatto i suoi colori se li tiene.
 *
 * <p>Lo si capisce dall'assenza della chiave salvata — ed è per questo che «Ripristina
 * predefinite» la CANCELLA invece di riscriverci sopra i valori di serie: altrimenti chi ripristina
 * risulterebbe «personalizzato» per sempre e i colori dell'azienda non lo raggiungerebbero mai.
 */
export function utenteHaPersonalizzato() {
    try {
        return localStorage.getItem(LAYOUT_STORAGE_KEY) != null;
    } catch {
        return false;
    }
}

/**
 * Applica un aspetto (di solito quello scelto dall'azienda) senza toccare ciò che l'utente ha
 * salvato: si passa `persisti = true` solo quando è lui a chiederlo esplicitamente.
 */
export function applicaLayout(valori, persisti = false) {
    if (!valori || typeof valori !== 'object') return;
    const consentite = Object.keys(LAYOUT_DEFAULTS);
    sospendiPersistenza = !persisti;
    try {
        for (const [chiave, valore] of Object.entries(valori)) {
            if (consentite.includes(chiave) && valore !== null && valore !== undefined) {
                layoutConfig[chiave] = valore;
            }
        }
    } finally {
        // Il watch scatta DOPO, sul prossimo tick: la sospensione va tolta lì, non subito.
        if (!persisti) queueMicrotask(() => { sospendiPersistenza = false; });
    }
    if (persisti) persistLayoutConfig();
}

// Idrata le impostazioni salvate (escluso darkTheme, gestito da dashStore) al boot.
try {
    const saved = JSON.parse(localStorage.getItem(LAYOUT_STORAGE_KEY) || 'null');
    if (saved && typeof saved === 'object') {
        const { darkTheme: _ignore, ...rest } = saved;
        Object.assign(layoutConfig, rest);
    }
} catch { /* config corrotta: si ignora e si usano i default */ }

// Persiste a ogni cambio (escluso darkTheme).
function persistLayoutConfig() {
    if (sospendiPersistenza) return;
    const { darkTheme: _d, ...rest } = layoutConfig;
    try { localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(rest)); } catch { /* quota/SSR */ }
}
watch(
    () => [layoutConfig.primary, layoutConfig.surface, layoutConfig.menuMode,
           layoutConfig.menuTheme, layoutConfig.topbarTheme, layoutConfig.menuProfilePosition],
    persistLayoutConfig
);

const layoutState = reactive({
    staticMenuInactive: false,
    overlayMenuActive: false,
    rightMenuActive: false,
    configSidebarVisible: false,
    mobileMenuActive: false,
    sidebarExpanded: false,
    menuHoverActive: false,
    activePath: null,
    anchored: false,
    topbarMenuActive: false,
    menuProfileActive: false
});

export function useLayout() {
    const route = useRoute();

    const toggleMenu = () => {
        if (isDesktop()) {
            if (layoutConfig.menuMode === 'static') {
                layoutState.staticMenuInactive = !layoutState.staticMenuInactive;
            }

            if (layoutConfig.menuMode === 'overlay') {
                layoutState.overlayMenuActive = !layoutState.overlayMenuActive;
            }
        } else {
            layoutState.mobileMenuActive = !layoutState.mobileMenuActive;
        }
    };

    const toggleConfigSidebar = () => {
        layoutState.configSidebarVisible = !layoutState.configSidebarVisible;
    };

    // Ripristina tutte le impostazioni del layout ai valori di default.
    // darkTheme torna a false → il watch in AppLayout sincronizza/persiste lo stato dark.
    /**
     * Torna ai valori di partenza e DIMENTICA la personalizzazione: da qui in avanti l'utente
     * riceverà di nuovo l'aspetto scelto dall'azienda, se ne ha uno.
     */
    const resetLayout = () => {
        sospendiPersistenza = true;
        try {
            localStorage.removeItem(LAYOUT_STORAGE_KEY);
            Object.assign(layoutConfig, LAYOUT_DEFAULTS);
        } catch { /* storage non disponibile */ }
        queueMicrotask(() => { sospendiPersistenza = false; });
    };

    const hideMobileMenu = () => {
        layoutState.mobileMenuActive = false;
    };

    const changeMenuMode = (menuMode) => {
        layoutConfig.menuMode = menuMode;
        layoutState.staticMenuInactive = false;
        layoutState.overlayMenuActive = false;
        layoutState.mobileMenuActive = false;
        layoutState.sidebarExpanded = false;
        layoutState.menuHoverActive = false;
        layoutState.anchored = false;
        layoutState.menuProfileActive = false;

        if (isDesktop()) {
            layoutState.activePath = hasOverlaySubmenu.value ? null : route.path;
        }
    };

    const isDarkTheme = computed(() => layoutConfig.darkTheme);
    const isDesktop = () => window.innerWidth > 991;
    const isHorizontal = computed(() => layoutConfig.menuMode === 'horizontal');
    const isSlim = computed(() => layoutConfig.menuMode === 'slim');
    const isSlimPlus = computed(() => layoutConfig.menuMode === 'slim-plus');

    const hasOverlaySubmenu = computed(() => layoutConfig.menuMode === 'slim' || layoutConfig.menuMode === 'slim-plus' || layoutConfig.menuMode === 'horizontal');
    const hasOpenOverlaySubmenu = computed(() => hasOverlaySubmenu.value && layoutState.activePath !== null && layoutState.activePath !== '/');
    const hasOpenOverlay = computed(() => layoutState.overlayMenuActive || hasOpenOverlaySubmenu.value);

    const isProfileMenuActive = computed(() => layoutState.menuProfileActive);

    return {
        layoutConfig,
        layoutState,
        isDarkTheme,
        toggleConfigSidebar,
        resetLayout,
        toggleMenu,
        hasOpenOverlay,
        hasOverlaySubmenu,
        hasOpenOverlaySubmenu,
        hideMobileMenu,
        changeMenuMode,
        isHorizontal,
        isSlim,
        isSlimPlus,
        isDesktop,
        isProfileMenuActive
    };
}
